import React from 'react';
import { SubscriptionTier } from 'src/api/billing/types';
import {
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  AutoAwesome as AutoAwesomeIcon,
  Reviews as ReviewsIcon,
  Campaign as CampaignIcon,
  InsertChart as InsertChartIcon,
  Tune as TuneIcon,
  Apps as AppsIcon,
  CreditCard as CreditCardIcon,
  Public as PublicIcon,
  QrCode2 as QrCode2Icon,
} from '@mui/icons-material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { brand } from 'src/themes/palette';

type NavItemConfig = {
  text: string;
  icon: React.ReactNode;
  path: string;
  baseEnabled?: boolean;
  minTier?: SubscriptionTier;
};

type NavSection = {
  section: string;
  items: NavItemConfig[];
};

const DEFAULT_SUBSCRIPTION_TIER: SubscriptionTier = 'tier1';

const tierRank: Record<SubscriptionTier, number> = {
  tier1: 1,
  tier2: 2,
  tier3: 3,
};

const navItems: NavSection[] = [
  {
    section: 'Overview',
    items: [
      {
        text: 'Dashboard',
        icon: <DashboardIcon sx={{ color: brand.primary }} />,
        path: '/home/dashboard',
        baseEnabled: true,
        minTier: 'tier3',
      },
    ],
  },
  {
    section: 'Insights & Automation',
    items: [
      {
        text: 'AI Assistant',
        icon: <AutoAwesomeIcon sx={{ color: brand.primary }} />,
        path: '/home/assistant',
        baseEnabled: true,
        minTier: 'tier3',
      },
      {
        text: 'Automations',
        icon: <TuneIcon sx={{ color: brand.primary }} />,
        path: '/home/automations',
        baseEnabled: true,
        minTier: 'tier3',
      },
      {
        text: 'Sales & Analytics',
        icon: <BarChartIcon sx={{ color: brand.primary }} />,
        path: '/home/analytics',
        baseEnabled: true,
        minTier: 'tier3',
      },
      {
        text: 'Expenses Tracker',
        icon: <ReceiptLongIcon sx={{ color: brand.primary }} />,
        path: '/home/expenses',
        baseEnabled: true,
        minTier: 'tier2',
      },
    ],
  },
  {
    section: 'Engagement',
    items: [
      {
        text: 'Statistics',
        icon: <PublicIcon sx={{ color: brand.primary }} />,
        path: '/home/keywords',
        baseEnabled: true,
        minTier: 'tier1',
      },
      {
        text: 'Check-ins',
        icon: <QrCode2Icon sx={{ color: brand.primary }} />,
        path: '/home/checkins',
        baseEnabled: true,
        minTier: 'tier1',
      },
      {
        text: 'Reviews',
        icon: <ReviewsIcon sx={{ color: brand.primary }} />,
        path: '/home/reviews',
        baseEnabled: true,
        minTier: 'tier1',
      },
      {
        text: 'Keyword Reports',
        icon: <InsertChartIcon sx={{ color: brand.primary }} />,
        path: '/home/reports',
        baseEnabled: true,
        minTier: 'tier1',
      },
      {
        text: 'Social Media Posts',
        icon: <CampaignIcon sx={{ color: brand.primary }} />,
        path: '/home/campaigns',
        baseEnabled: true,
        minTier: 'tier2',
      },
    ],
  },
  {
    section: 'System',
    items: [
      {
        text: 'Connect Apps',
        icon: <AppsIcon sx={{ color: brand.primary }} />,
        path: '/home/connect',
        baseEnabled: true,
        minTier: 'tier2',
      },
      {
        text: 'Settings',
        icon: <SettingsIcon sx={{ color: brand.primary }} />,
        path: '/home/settings',
        baseEnabled: true,
        minTier: 'tier1',
      },
      {
        text: 'Billing',
        icon: <CreditCardIcon sx={{ color: brand.primary }} />,
        path: '/home/billing',
        baseEnabled: true,
        minTier: 'tier1',
      },
    ],
  },
];

const normalizeSubscriptionTier = (
  raw?: string | null,
): SubscriptionTier | undefined => {
  if (!raw) return undefined;
  const key = raw
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  const aliases: Record<string, SubscriptionTier> = {
    tier1: 'tier1',
    '1': 'tier1',
    basic: 'tier1',
    starter: 'tier1',
    essentials: 'tier1',
    essential: 'tier1',
    growth: 'tier2', // plan id for Growth
    tier2: 'tier2',
    '2': 'tier2',
    scale: 'tier2',
    standard: 'tier2',
    pro: 'tier2',
    growthplan: 'tier2',
    tier3: 'tier3',
    '3': 'tier3',
    enterprise: 'tier3',
    elite: 'tier3',
    everything: 'tier3',
  };
  return aliases[key];
};

const isEnabledForTier = (tier: SubscriptionTier, item: NavItemConfig) => {
  const baseEnabled = item.baseEnabled ?? true;
  const requiredTier = item.minTier ?? DEFAULT_SUBSCRIPTION_TIER;
  return baseEnabled && tierRank[tier] >= tierRank[requiredTier];
};

const fetchNavItems = (tier: SubscriptionTier = DEFAULT_SUBSCRIPTION_TIER) => {
  return navItems.map(section => ({
    ...section,
    items: section.items.map(item => ({
      text: item.text,
      icon: item.icon,
      path: item.path,
      enabled: isEnabledForTier(tier, item),
    })),
  }));
};

export { DEFAULT_SUBSCRIPTION_TIER, fetchNavItems, normalizeSubscriptionTier };
